import React, {useState} from "react";
import { motion } from "framer-motion";
import {
  EyeSlashIcon,
  Cog6ToothIcon,
  ClockIcon,
  BookOpenIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

//25 Nov 2025
import EtModalContactConversation from "@/components/modal/EtModalContactConversation";

// Placeholder abstract image URLs (replace with your own assets later)
// import mindwave1 from "@/assets/img/pics/brainImg1.png";
// import mindwave2 from "@/assets/img/pics/brainImg2.png";
// import mindwave3 from "@/assets/img/pics/brainImg3.png";
// import mindwave4 from "@/assets/img/pics/ceoImg2.png";
// import mindwave5 from "@/assets/img/pics/ceoImg3.png";
import mindwave1 from "@/assets/img/pics/web2/mindState1.png";
import mindwave2 from "@/assets/img/pics/web2/mindState2.png";
import mindwave3 from "@/assets/img/pics/web2/mindState3.png";
import mindwave4 from "@/assets/img/pics/web2/mindState4.png";
import mindwave5 from "@/assets/img/pics/web2/mindState5.png";


const mindPatterns = [
  {
    id: "comfort-familiar",
    icon: EyeSlashIcon,
    title: "Comfort in the Familiar",
    desc: "Success so far makes new playbooks feel unnecessary.",
    reflection: "Are you refining what you know, or reinventing what’s possible?",
    image: mindwave1,
  },
  {
    id: "overconfidence-control",
    icon: Cog6ToothIcon,
    title: "Overconfidence in Control",
    desc: "A belief that only constant personal drive ensures outcomes.",
    reflection: "Do you trust your system or your stamina?",
    image: mindwave2,
  },
  {
    id: "short-term-reflex",
    icon: ClockIcon,
    title: "Short-Term Reflex",
    desc: "Urgency replaces priority; attention gets trapped in the immediate.",
    reflection: "How often do you make space for the long view?",
    image: mindwave3,
  },
  {
    id: "underinvestment-reflection",
    icon: BookOpenIcon,
    title: "Underinvestment in Reflection",
    desc: "Thinking time feels indulgent amidst execution.",
    reflection: "What’s the cost of never stopping to reframe?",
    image: mindwave4,
  },
  {
    id: "resistance-unlearning",
    icon: ArrowPathIcon,
    title: "Resistance to Unlearning",
    desc: "Old strengths become blind spots.",
    reflection: "Are your habits serving your horizon, or holding it back?",
    image: mindwave5,
  },
];

export default function EtClientsInnerStates() {
  // First Created - 18 Oct 2025
  // Author - Atul Pandey
  // Refined - 19 Oct 2025 (Dual Horizon Edition - visual transition from dark to light)
  // Updated - 25 Nov 2025 - Refined for Unified CTA - ctaSource
      
  // 1 -  Local UI state 
  const [open, setOpen] = useState({ visible: false, source: null }); // 25 Nov 2025 - unified CTA

  return (
    <section id="inner-states" className="relative overflow-hidden">
      {/* --- Magnetic Gradient Field Base --- */}
      <div className="relative bg-gradient-to-b from-[#1a0f0a] via-[#120907] to-black text-gray-100 pt-40 pb-28">
        {/* Pulsing energy fields (section-wide) */}
        <motion.div
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-700/20 blur-3xl"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-500/15 blur-3xl"
          animate={{ opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 12, repeat: Infinity }}
        />

        {/* Section Header */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20">
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-6 text-orange-500"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* What Limits Growth Isn’t Strategy — It’s Mind State */}
            {/* What Limits Growth Isn’t Strategy — It’s Mind Frame */}
            What Limits Growth Isn’t Strategy. It’s Mind Frame
          </motion.h2>

          <motion.p
            className="text-gray-300 max-w-xl mx-auto text-2xl leading-relaxed"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Behind every <span className="text-orange-600">visible</span> state of growth lies an <span className="text-orange-600">invisible</span> pattern. A way of
            <span className="text-orange-600 animate-pulse"> thinking</span> that either compounds progress or quietly constrains it.
          </motion.p>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {mindPatterns.map((m, i) => (
              <motion.div
                key={m.id}
                className={`flex flex-col md:flex-row items-start gap-10 md:gap-16 text-left relative border-t border-orange-900/30 pt-12 ${
                  i % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.7 }}
              >
                {/* Icon + Title */}
                <div className={`md:w-1/3 ${i % 2 === 1 ? "md:text-right" : ""}`}>
                  <m.icon
                    className={`h-8 w-8 text-orange-500 mb-3 ${
                      i % 2 === 1 ? "ml-auto" : ""
                    }`}
                  />
                  <h3 className="text-3xl font-semibold mb-2">{m.title}</h3>
                  <p className="text-gray-400 italic text-lg">{m.desc}</p>
                </div>

                {/* Reflection with centered text */}
                {/* <div
                className={`relative md:w-2/3 text-gray-100 text-base md:text-lg leading-relaxed overflow-hidden rounded-xl flex items-center ${
                    i % 2 === 1
                    ? "md:text-right md:border-r-2 md:border-orange-700/40 pr-6"
                    : "md:border-l-2 md:border-orange-700/40 pl-6"
                } min-h-[240px] md:min-h-[300px]`}
                > */}
                <div
                className={`relative aspect-square md:aspect-[16/9] w-full text-gray-100 text-base md:text-lg leading-relaxed overflow-hidden rounded-xl flex items-center ${
                    i % 2 === 1
                    ? "md:text-right md:border-r-2 md:border-orange-700/40 pr-6"
                    : "md:border-l-2 md:border-orange-700/40 pl-6"
                } min-h-[240px] md:min-h-[360px]`}
                >
                {/* Background image layer */}
                <motion.img
                    src={m.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover md:object-contain opacity-10 rounded-xl"
                    animate={{ opacity: [0.08, 0.14, 1] }}
                    transition={{ duration: 10 + i * 1.5, repeat: Infinity }}
                />

                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/40" />

                {/* Centered reflection text */}
                <div className="relative z-10 w-full text-center px-4 py-8 md:px-8 md:py-12 flex justify-center items-center">
                    <p className="max-w-lg text-2xl">{m.reflection}</p>
                </div>
                </div>

                

              </motion.div>
            ))}
          </div>

          {/* Closing Reflection + CTA */}
          <motion.div
            className="text-center mt-32 relative"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            {/* Section glow behind CTA */}
            <motion.div
              className="absolute -z-10 inset-0 flex justify-center"
              animate={{ opacity: [0.1, 0.3, 0.1] }}
              transition={{ duration: 12, repeat: Infinity }}
            >
              <div className="w-96 h-96 bg-orange-700/20 blur-3xl rounded-full" />
            </motion.div>

            <p className="text-gray-300 max-w-3xl mx-auto font-light text-xl md:text-2xl leading-relaxed mb-10">
              Transformation begins with awareness. Every leader’s path is unique.
              And the next one starts with your own discovery. We desined <span className="text-orange-600 font-bold animate-pulse">Trinity</span> precisely to do just that!
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                // href="#contact"
                onClick={() => setOpen({ visible: true, source: "clients-mindpatterns" })} // 25 Nov 2025
                className="inline-block px-8 py-3 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700 transition cursor-pointer"
              >
                Begin Your Discovery
              </a>
              <a
                // href="#testimonials"
                href="/trinity/meet"
                className="inline-block px-8 py-3 rounded-lg bg-orange-950/40 text-orange-200 font-semibold hover:bg-orange-800/60 transition"
              >
                {/* Hear Voices of Transformation */}
                Meet Trinity!
              </a>
            </div>
          </motion.div>
        </div>
      </div>
      {/* 25 Nov 2025 */}
      {open.visible && (
        <EtModalContactConversation
          open={open}
          onClose={() => setOpen({ visible: false, source: null })}
        />
      )}
    </section>
  );
}